import React, { useEffect, useState, useRef } from "react";
import CloseIcon from "@mui/icons-material/Close";
import {
  Card,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Dialog,
  Checkbox,
  TextField,
} from "@mui/material";
import { t } from "i18next";
import { CATEGORY_CODE, TARGET_TYPES } from "../../../constants/enums";
import { primaryColor } from "../../../constants/colorConstants";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import dayjs from "dayjs";
import { DatePicker } from "antd";
import { ArrowLeft, ArrowRight } from "@mui/icons-material";
import "../EmissionTarget.scss";

const CategoryLevelDialog = (props: any) => {
  const {
    open,
    onClose,
    categoryList,
    scope,
    type,
    targetState,
    baselineYears,
    targetYears,
    addBaseline,
    addTarget,
    getAvailableCategories,
    categoryData,
    setCategoryData,
    updateTarget,
    targetRows,
    handleTableCellClick,
    categoryAttachments,
    selectedIndex,
    rowIdx,
    disabledDate,
    disableTargetYear,
  } = props;

  const [cellColumns, setCellColumns] = useState<string[]>([]);

  const baselineColumns = [
    `Baseline year Scope ${scope}`,
    "Emissions(tCO2e)",
    "Included",
    "",
    "Comments",
  ];
  const targetColumns = [
    `Target year Scope ${scope}`,
    "Emissions(tCO2e)",
    "%SBT reduction",
    "",
    "Comments",
    "",
  ];

  useEffect(() => {
    setCellColumns(
      type === TARGET_TYPES.BASELINE ? baselineColumns : targetColumns
    );
  }, [type, scope]);

  const hasCategoryDataUpdated = useRef(false);

  const [categoryIndex, setCategoryIndex] = useState(0);

  const [editableCell, setEditableCell] = useState<{
    rowId: number;
    // column: string;
  } | null>(null);
  const [editedValue, setEditedValue] = useState<number | string>("");
  // const [currentBaseline, setCurrentBaseline] = useState<any>({});

  useEffect(() => {
    const baseline = baselineYears.find(
      (baseline: any) => baseline.categoryId === categoryIndex
    );
    // setCurrentBaseline(baseline);

    if (hasCategoryDataUpdated.current) {
      getAvailableCategories(
        [],
        TARGET_TYPES.BASELINE,
        baseline.year,
        baseline.scope,
        baseline.categoryId,
        baseline.included
      );
      hasCategoryDataUpdated.current = false;
    }
  }, [categoryData, categoryIndex]);

  const handleDoubleClick = (
    rowId: number,
    // column: string,
    value: number | string
  ) => {
    setEditableCell({ rowId });
    setEditedValue(value);
  };

  const handleValueChange = (event: any) => {
    if (/^\d*\.?\d*$/.test(event.target.value)) {
      setEditedValue(event.target.value);
    }
  };

  const updateCategoryData = (
    rowId: number,
    updateFn: (category: any) => void
  ) => {
    setCategoryData((prevData: Map<string, any[]>) => {
      const updatedData = new Map(prevData);
      updatedData.forEach((categories, key) => {
        const categoryIndex = categories.findIndex(
          (category) => category.id === rowId
        );
        if (categoryIndex !== -1) {
          updateFn(categories[categoryIndex]);
          updatedData.set(key, categories);
          hasCategoryDataUpdated.current = true;
        }
      });
      setCategoryIndex(rowId);
      return updatedData;
    });
  };

  const handleValueSave = (rowId: number, column: string) => {
    updateCategoryData(rowId, (category) => {
      category[column] = editedValue !== "" ? editedValue : 0;
    });
    setEditableCell(null);
  };

  const handleCheckboxChange = (rowId: number, checked: boolean) => {
    updateCategoryData(rowId, (category) => {
      category.included = checked;
    });
    const baseline = baselineYears.find(
      (baseline: any) => baseline.categoryId === rowId
    );
    updateTarget(
      baseline.year,
      0,
      baseline.value,
      0,
      false,
      null,
      baseline?.comment,
      baseline?.attachments || [],
      TARGET_TYPES.BASELINE,
      checked,
      rowIdx,
      rowId
    );
  };

  useEffect(() => {
    if (categoryAttachments) {
      const baseline = baselineYears.find(
        (baseline: any) => baseline.categoryId === categoryIndex
      );
      updateCategoryData(categoryIndex, (category) => {
        category.attachments = baseline.attachments;
      });
    }
  }, [categoryAttachments]);

  const handleCommentChange = (rowId: number, comment: string) => {
    updateCategoryData(rowId, (category) => {
      category.comments = comment;
    });
    const baseline = baselineYears.find(
      (baseline: any) => baseline.categoryId === rowId
    );
    updateTarget(
      baseline.year,
      0,
      baseline.value,
      0,
      false,
      null,
      comment,
      baseline?.attachments || [],
      TARGET_TYPES.BASELINE,
      baseline?.included,
      rowIdx,
      rowId
    );
  };

  const shouldDisplayCategory = (code: CATEGORY_CODE) => {
    if (scope === 1) {
      return (
        code >= CATEGORY_CODE.CATEGORY_1_1 && code < CATEGORY_CODE.CATEGORY_2_1
      );
    } else if (scope === 2) {
      return (
        code >= CATEGORY_CODE.CATEGORY_2_1 && code < CATEGORY_CODE.CATEGORY_3_1
      );
    } else if (scope === 3) {
      return code >= CATEGORY_CODE.CATEGORY_3_1;
    }
  };
  const renderBaselineRows = () => {
    const categories = categoryList || [];

    return categories
      .filter((eachCategory: { code: CATEGORY_CODE }) =>
        shouldDisplayCategory(eachCategory.code)
      )
      .map(
        (eachCategory: {
          id: number;
          code: string | number | undefined;
          name: string;
          value: number;
          included: boolean;
        }) => {
          const relevantBaseline = baselineYears.find(
            (baseline: { categoryId: any }) =>
              baseline.categoryId === eachCategory.id
          );

          return (
            <React.Fragment key={eachCategory.id}>
              <TableRow>
                <TableCell
                  colSpan={5}
                  className="pw-activity-list bg-neutral-50"
                >
                  {eachCategory.code} {eachCategory.name}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell className="pw-activity-list">
                  <DatePicker
                    value={
                      relevantBaseline
                        ? dayjs(relevantBaseline?.year.toString(), "YYYY")
                        : null
                    }
                    id="pw-baseline-dropdown"
                    onChange={(date, dateString) => {
                      addBaseline(
                        parseInt(dateString),
                        selectedIndex,
                        rowIdx,
                        eachCategory.id
                      );
                    }}
                    picker="year"
                    disabledDate={disabledDate}
                    suffixIcon={<ArrowDropDownIcon />}
                    getPopupContainer={(trigger) =>
                      trigger.parentNode as HTMLElement
                    }
                    disabled={!targetState?.businessUnitName}
                  />
                </TableCell>
                <TableCell
                  className={`bg-total-row pw-cat-emissionVal-${eachCategory.id} pw-no-focus-outline`}
                  align="right"
                  onClick={() =>
                    handleDoubleClick(
                      eachCategory.id,

                      relevantBaseline.value || 0
                    )
                  }
                  onKeyDown={(e) => {
                    // if (e.key === "Enter") {
                    //   handleDoubleClick(
                    //     eachCategory.id,
                    //     "value",
                    //     eachCategory.value || 0
                    //   );
                    // }
                  }}
                  tabIndex={0}
                >
                  {editableCell && editableCell.rowId === eachCategory.id ? (
                    <TextField
                      value={editedValue}
                      onChange={handleValueChange}
                      onBlur={() => handleValueSave(eachCategory.id, "value")}
                      inputProps={{
                        style: { padding: "0 8px", width: "fixed" },
                      }}
                      autoFocus
                    />
                  ) : relevantBaseline?.value ? (
                    relevantBaseline?.value
                  ) : (
                    0
                  )}
                </TableCell>
                <TableCell
                  align="center"
                  className="pw-no-focus-outline"
                  tabIndex={0}
                >
                  <Checkbox
                    sx={{
                      color: "#8897AE",
                      "&.Mui-checked": { color: primaryColor },
                    }}
                    disabled={!relevantBaseline?.year}
                    checked={relevantBaseline?.included ? true : false}
                    onChange={(e) =>
                      handleCheckboxChange(eachCategory.id, e.target.checked)
                    }
                  />
                </TableCell>
                <TableCell
                  onClick={
                    relevantBaseline?.year
                      ? () => {
                          setCategoryIndex(eachCategory.id);
                          handleTableCellClick(
                            relevantBaseline?.year,
                            TARGET_TYPES.BASELINE,
                            relevantBaseline?.attachments || [],
                            relevantBaseline?.comment,
                            eachCategory.id
                          );
                        }
                      : () => {}
                  }
                  className={`${
                    relevantBaseline?.year
                      ? "cursor-pointer"
                      : "cursor-not-allowed"
                  } pw-no-focus-outline`}
                  tabIndex={0}
                >
                  <img src="assets/icons/FileUpload.svg" alt="Upload" />
                  {relevantBaseline?.attachments &&
                    relevantBaseline?.attachments.length > 0 && (
                      <sup className="text-xs bg-primary badge">
                        {relevantBaseline?.attachments?.length}
                      </sup>
                    )}
                </TableCell>
                <TableCell className="pw-no-focus-outline" tabIndex={0}>
                  <textarea
                    name="comment"
                    placeholder="Enter Comment"
                    className="pw-textarea"
                    value={relevantBaseline?.comment || ""}
                    disabled={!relevantBaseline?.year}
                    onChange={(e) =>
                      handleCommentChange(eachCategory.id, e.target.value)
                    }
                  ></textarea>
                </TableCell>
              </TableRow>
            </React.Fragment>
          );
        }
      );
  };

  const [currentCategoryIdx, setCurrentCategoryIdx] = useState(0);

  const renderTargetRows = () => {
    const categories = categoryList || [];

    const filteredCategories = categories.filter((category: any) => {
      const baseline = baselineYears.find(
        (baseline: { rowIdx: number; categoryId: number; included: boolean }) =>
          baseline.categoryId === category.id && baseline.included
      );
      return baseline !== undefined && shouldDisplayCategory(category.code);
    });

    const handlePrevClick = () => {
      if (currentCategoryIdx > 0) {
        setCurrentCategoryIdx(currentCategoryIdx - 1);
      }
    };

    const handleNextClick = () => {
      if (currentCategoryIdx < filteredCategories.length - 1) {
        setCurrentCategoryIdx(currentCategoryIdx + 1);
      }
    };

    const currentCategory = filteredCategories[currentCategoryIdx];
    const targetYrs = targetYears.filter(
      (targetYr: { rowIdx: any; categoryId: any }) =>
        targetYr.rowIdx === rowIdx &&
        targetYr.categoryId === currentCategory?.id
    );

    const relevantBaseline = baselineYears.find(
      (baseline: { categoryId: any }) =>
        baseline.categoryId === currentCategory?.id
    );

    return (
      <>
        {filteredCategories.length > 0 ? (
          <>
            <TableRow key={currentCategory.id}>
              <TableCell colSpan={6} className="pw-activity-list bg-neutral-50">
                {currentCategory.code} {currentCategory.name}
              </TableCell>
            </TableRow>
            {relevantBaseline?.included ? (
              <>{targetRows({ targetYrs, rowIdx })}</>
            ) : (
              <TableCell></TableCell>
            )}
            <TableRow>
              <TableCell
                className={
                  relevantBaseline && !relevantBaseline?.included
                    ? "pw-neutral-background-400"
                    : ""
                }
              >
                <DatePicker
                  value={null}
                  placeholder="Select Year"
                  id="pw-target-dropdown"
                  className="pw-datepicker"
                  onChange={(date, dateString) => {
                    addTarget(
                      parseInt(dateString),
                      selectedIndex,
                      rowIdx,
                      currentCategory.id
                    );
                  }}
                  picker="year"
                  disabledDate={disableTargetYear(rowIdx, currentCategory.id)}
                  suffixIcon={<ArrowDropDownIcon />}
                  getPopupContainer={(trigger) =>
                    trigger.parentNode as HTMLElement
                  }
                  disabled={
                    targetState.targets
                      .filter(
                        (target: { type: string; rowIdx: any; year: any }) =>
                          target.type === TARGET_TYPES.BASELINE &&
                          target.rowIdx === rowIdx
                      )
                      .every((target: { year: any }) => !target.year) ||
                    (relevantBaseline && !relevantBaseline?.included)
                  }
                />
              </TableCell>
              {/* {Array.from({ length: 5 }).map((_, idx) => (
                <TableCell
                  key={idx}
                  className={
                    relevantBaseline && !relevantBaseline?.included
                      ? "pw-neutral-background-400"
                      : ""
                  }
                />
              ))} */}
            </TableRow>
            <TableRow
              key={`${currentCategory.id}-navigation`}
              className="navigation-row"
            >
              <TableCell
                colSpan={6}
                className="pw-activity-list bg-neutral-50 pw-navigation-container"
              >
                <div className="pw-navigation-content">
                  {currentCategoryIdx > 0 && (
                    <div className="flex flex-row gap-2">
                      <div className="text-primary">Previous Category</div>
                      <div
                        onClick={handlePrevClick}
                        className="pw-navigate-next pw-padding-5"
                      >
                        <ArrowLeft />
                      </div>
                    </div>
                  )}
                  {currentCategoryIdx < filteredCategories.length - 1 && (
                    <div className="flex flex-row ml-auto gap-2">
                      <div className="text-primary">Next Category</div>
                      <div
                        onClick={handleNextClick}
                        className="pw-navigate-next pw-padding-5"
                      >
                        <ArrowRight />
                      </div>
                    </div>
                  )}
                </div>
              </TableCell>
            </TableRow>
          </>
        ) : (
          <TableRow>
            <TableCell colSpan={6}>Baseline year not defined</TableCell>
          </TableRow>
        )}
      </>
    );
  };

  return (
    <>
      <Dialog open={open} className="pw-category-target-dialog">
        <div className="dialog-content">
          <div className="dialog-header">
            <div>
              {type === TARGET_TYPES.BASELINE ? "Baseline" : "Target"} Year
              details
            </div>
            <div onClick={onClose} className="close-icon">
              <CloseIcon />
            </div>
          </div>
          <div className="dialog-body">
            <Card>
              <Table size="small" className="pw-activity-table">
                <TableHead className="pw-activity-table-head">
                  <TableRow>
                    {cellColumns.map((eachCell: any, index: number) => (
                      <TableCell
                        key={index}
                        className={`pw-activity-list ${
                          type === "BASELINE"
                            ? index === 0
                              ? "w-[446px]"
                              : index === 1
                              ? "w-[212px]"
                              : index === 2
                              ? "w-[50px]"
                              : index === 3
                              ? "w-[50px]"
                              : "w-[250px]"
                            : type === "TARGET"
                            ? index === 0
                              ? "w-[375px]"
                              : index === 1
                              ? "w-[126px] bg-total-row"
                              : index === 2
                              ? "w-[157px] bg-total-row"
                              : index === 3
                              ? "w-[50px]"
                              : index === 4
                              ? "w-[203px]"
                              : "w-[158px]"
                            : ""
                        }`}
                        style={{ borderRight: "10px black" }}
                        align={index === 1 ? "center" : "left"}
                      >
                        {t(eachCell)}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {type === TARGET_TYPES.BASELINE
                    ? renderBaselineRows()
                    : renderTargetRows()}
                </TableBody>
              </Table>
            </Card>
          </div>
        </div>
      </Dialog>
    </>
  );
};

export default CategoryLevelDialog;
